import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  Output
} from '@angular/core';
import { BaseTask, User } from '@ee/common/models';
import { AccountType } from '@ee/common/enums';
import { OverlayService } from '@ee/common/services';

@Component({
  selector: 'ee-workflow-task',
  template: `
    <div class="workflow-task" (click)="onShowControls()">
      <div class="inline-block">
        This step {{ task.days_remaining < 0 ? 'was' : 'is' }} due
        <span class="task-due-date ml-1" [ngClass]="{ emphasize: isOverdue }">
          {{ $safeNavigationMigration(task?.due_date) | dateTimeFromIso | dateTimeToFormat: 'MMM d, yyyy' }}
        </span>
        @if (!!task.days_remaining && task.days_remaining < 8) {
          <span [ngClass]="{ emphasize: isOverdue }">
            @if (task.days_remaining > 0) {
              <span>({{ task.days_remaining }} day{{ task.days_remaining > 1 ? 's' : '' }} left)</span>
            }
            @if (task.days_remaining === 0) {
              <span>(Due today)</span>
            }
            @if (task.days_remaining < 0) {
              <span>({{ task.days_remaining * -1 }} day{{ task.days_remaining < -1 ? 's' : '' }} overdue)</span>
            }
          </span>
        }
      </div>
      @if (assignedUsers?.length) {
        <div class="flex flex-row justify-center items-center">
          <div class="mr-1">and is assigned to</div>
          @for (user of assignedUsers; track user) {
            <span
              class="small"
              [tp]="user.display_name"
              tpPlacement="top"
              [tpDelay]="500"
              [attr.data-letters]="user.display_name | dataLetters"
              [ngStyle]="{ '--user-selector-color': user.color }"></span>
          }
        </div>
      }
      @if (showControls) {
        <div class="edit-overlay">
          <button
            mat-icon-button
            class="ee__button ee__large md-gray-bg mr-4"
            aria-label="Exit"
            (click)="onHideControls($event)"
            tp="Hide Options"
            type="button">
            <mat-icon fontSet="fa" fontIcon="fa-x"></mat-icon>
          </button>
          <ee-workflow-task-info [task]="task" [users]="users" (onSave)="save($event)"></ee-workflow-task-info>
          <button
            class="ee__button ee__large md-gray-bg mr-4"
            mat-icon-button
            type="button"
            (click)="onDelete.emit(task)"
            tp="Remove Due Date">
            <mat-icon fontSet="fa" fontIcon="fa-trash"></mat-icon>
          </button>
        </div>
      }
    </div>
  `,
  styles: [
    `
      .workflow-task {
        position: relative;
        color: var(--color-on-surface);
        cursor: pointer;
        user-select: none;
        font-size: 16px;
        font-weight: 600;
        text-align: center;
        padding-top: 1rem;
        padding-bottom: 1rem;
        background-color: var(--color-surface-container-high);

        &:hover {
          background-color: var(--color-surface-container-highest);
        }

        .edit-overlay {
          display: flex;
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background-color: var(--color-surface-container-high);
          flex-direction: row;
          justify-content: center;
          align-items: center;
          border-radius: 4px;
        }

        .task-due-date {
          font-weight: 600;
          text-wrap: nowrap;
          align-self: start;
        }
      }

      .emphasize {
        font-weight: 600;
        color: var(--primary-red);
      }

      .save-users-button {
        text-align: center;
        background-color: var(--primary-color);
        color: #fff;

        &:hover {
          color: var(--dark-gray);
        }
      }
    `
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class WorkflowTaskComponent implements OnInit, OnChanges {
  public isOverdue = false;
  public showControls = false;
  public assignedUsers: User[] = [];

  @Input({ required: true })
  public task: BaseTask;

  @Input()
  public users: User[] = [];

  @Output()
  public onDelete = new EventEmitter<BaseTask>();

  @Output()
  public onUpdateTask = new EventEmitter<BaseTask>();

  constructor(
    private eRef: ElementRef,
    private overlayService: OverlayService,
    private changeDetectorRef: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.isOverdue = this.task.days_remaining && this.task.days_remaining <= 3;
  }

  ngOnChanges() {
    this.assignedUsers = this.users
      ?.filter((user) => this.task.assigned_users.includes(user.id))
      .sort((a, b) => a.display_name.localeCompare(b.display_name));
    this.changeDetectorRef.markForCheck();
  }

  onShowControls() {
    this.showControls = true;
  }

  onHideControls($event) {
    this.showControls = false;
    $event.stopPropagation();
  }

  save(task: BaseTask) {
    this.onUpdateTask.emit(task);
    this.showControls = false;
  }

  isSelected(user: User): boolean {
    return this.task.assigned_users.includes(user.id);
  }

  @HostListener('document:click', ['$event'])
  clickout(event) {
    if (!this.eRef.nativeElement.contains(event.target) && !this.overlayService.isOverlayOpen()) {
      this.showControls = false; // hide controls if clicked outside of component
    }
  }

  protected readonly AccountType = AccountType;
}
